import mapKeys from 'lodash-es/mapKeys';

export type ListAction<T extends { id: string; name: string }> =
  | { type: 'setList'; items: T[] }
  | { type: 'add'; item: T }
  | { type: 'update'; item: T }
  | { type: 'remove'; id: string }
  | { type: 'reset' };

interface ListStateKeys {
  list: string;
  byId: string;
  byName: string;
}

function buildIndexMaps<T extends { id: string; name: string }>(items: T[]) {
  return {
    byId: mapKeys(items, 'id'),
    byName: mapKeys(items, 'name'),
  };
}

export function listReducer<T extends { id: string; name: string }>(
  state: Record<string, unknown>,
  action: ListAction<T>,
  keys: ListStateKeys,
): Record<string, unknown> {
  const list = (state[keys.list] as T[] | undefined) ?? [];

  switch (action.type) {
    case 'setList': {
      const indexes = buildIndexMaps(action.items);
      return {
        [keys.list]: action.items,
        [keys.byId]: indexes.byId,
        [keys.byName]: indexes.byName,
      };
    }
    case 'add': {
      const items = [...list, action.item];
      const indexes = buildIndexMaps(items);
      return {
        [keys.list]: items,
        [keys.byId]: indexes.byId,
        [keys.byName]: indexes.byName,
      };
    }
    case 'update': {
      const items = list.map(item => (item.id === action.item.id ? action.item : item));
      const indexes = buildIndexMaps(items);
      return {
        [keys.list]: items,
        [keys.byId]: indexes.byId,
        [keys.byName]: indexes.byName,
      };
    }
    case 'remove': {
      const items = list.filter(item => item.id !== action.id);
      const indexes = buildIndexMaps(items);
      return {
        [keys.list]: items,
        [keys.byId]: indexes.byId,
        [keys.byName]: indexes.byName,
      };
    }
    case 'reset':
      return {
        [keys.list]: [],
        [keys.byId]: undefined,
        [keys.byName]: undefined,
      };
    default:
      return {};
  }
}
